import { AppContextState } from '../contexts/AppContext.d'
import { INSTANCE_CONTROL_STARTING, INSTANCE_CONTROL_STOPPING, STATUS_ICON_WORKING, STATUS_PREPARE_PENDING,
    InstanceStatusCode, SettingsContextState } from '../contexts/SettingsContext.d'
import { actionInProgress, actionQueryCompleted, conditionalDelayedDispatch, conditionalDispatch } from '../helpers/action/action'
import { ApiResponseAction, Dispatch, PrimaryAction } from '../helpers/action/Action.d'
import { CheckInstanceAction, SettingsAction, SettingsInstanceId,
    SETTINGS_SYNC_APIS, SETTINGS_INSTANCE_IDS, SETTINGS_INSTANCE_IDS_AUTOCHECK } from '../helpers/action/SettingsAction.d'

// How long to wait between automatic status checks.
export const PREPARE_INTERVAL_SECONDS = 20
export const INSTANCE_INTERVAL_SECONDS = 5

export const checkInstanceAction = (instance: SettingsInstanceId): CheckInstanceAction => ({
    type: instance === 'prepare' ? 'CHECK_PREPARE_STATUS' : 'CHECK_INSTANCE_STATE',
    instanceId: instance
})

export const isInstanceStatusChanging = (
    instance: SettingsInstanceId | CheckInstanceAction | ApiResponseAction<CheckInstanceAction>,
    state: SettingsContextState): boolean => {
    const status = instanceStatus(instance, state)
    const fluxStatuses: InstanceStatusCode[] = [
        STATUS_ICON_WORKING, STATUS_PREPARE_PENDING,
        INSTANCE_CONTROL_STARTING, INSTANCE_CONTROL_STOPPING]
    return !!status && fluxStatuses.includes(status)
}

export const instanceStatus = (
    instance: SettingsInstanceId | CheckInstanceAction | ApiResponseAction<CheckInstanceAction>,
    state: SettingsContextState
): InstanceStatusCode | undefined => {
    const id = typeof instance === 'string'
        ? instance
        : 'sourceAction' in instance
            ? instance.sourceAction.instanceId
            : instance.instanceId
    // A batched check (array of ids) has no single status of its own.
    return Array.isArray(id) ? undefined : state.instances?.[id]
}

// An instance is "checking" if its own status query is in flight, or it's part of the in-flight batch.
export const instanceCheckInProgress = (instanceId: SettingsInstanceId, state: SettingsContextState): boolean =>
    actionInProgress({ type: 'CHECK_INSTANCE_STATE', instanceId }, state)
    || (SETTINGS_INSTANCE_IDS_AUTOCHECK.includes(instanceId)
      && actionInProgress({ type: 'CHECK_INSTANCE_STATE', instanceId: SETTINGS_INSTANCE_IDS_AUTOCHECK }, state))

// Run on loading the Admin page.
export const triggerInstanceChecks = (state: SettingsContextState, dispatch: Dispatch<SettingsAction>, admin = true): void => {
    if (state.windowFocused === false) {
        return
    }
    let batchNeeded = false
    for (const instanceId of SETTINGS_INSTANCE_IDS) {
        if (instanceId !== 'prepare' && !admin) {
            continue
        }
        const changing = isInstanceStatusChanging(instanceId, state)
        const autoLoad = !instanceStatus(instanceId, state)
          && (instanceId === 'prepare' || SETTINGS_INSTANCE_IDS_AUTOCHECK.includes(instanceId))
        if (!changing && !autoLoad) {
            continue
        }
        // prepare (its own lambda) and in-flux instances poll individually; resting ones query as one fixed batch.
        if (instanceId === 'prepare' || changing) {
            conditionalDispatch(checkInstanceAction(instanceId), state, dispatch, {
                statusResetDelay: instanceId === 'prepare' ? PREPARE_INTERVAL_SECONDS : INSTANCE_INTERVAL_SECONDS
            })
        } else {
            batchNeeded = true
        }
    }
    if (batchNeeded) {
        conditionalDispatch({ type: 'CHECK_INSTANCE_STATE', instanceId: SETTINGS_INSTANCE_IDS_AUTOCHECK }, state, dispatch,
            { statusResetDelay: INSTANCE_INTERVAL_SECONDS })
    }
}

export const ensureUserSnippets = (state: SettingsContextState, dispatch: Dispatch<SettingsAction>): void => {
    if (!actionQueryCompleted({ type: 'RETRIEVE_SNIPPETS' }, state)) {
        conditionalDispatch({ type: 'RETRIEVE_SNIPPETS' }, state, dispatch)
    }
    if (state.snippetsPending?.modify) {
        for (const action of Object.values(state.snippetsPending.modify)) {
            conditionalDispatch({ ...action, type: 'MODIFY_SNIPPET' }, state, dispatch)
        }
    }
    if (state.snippetsPending?.create) {
        for (const snippets of Object.values(state.snippetsPending.create)) {
            for (const snippet of Object.values(snippets)) {
                conditionalDispatch({ type: 'CREATE_SNIPPET', snippet }, state, dispatch)
            }
        }
    }
}

// Run at the AppContext level.
export const saveUserFields = (state: AppContextState, dispatch: Dispatch<PrimaryAction>): void => {
    if (!state.user?.row || !state.user.updated) {
        return
    }
    for (const field in state.user.updated) {
        saveUserField(field, state, dispatch)
    }
}

const saveUserField = (field: string, state: AppContextState, dispatch: Dispatch<PrimaryAction>): void => {
    if (state?.user?.updated?.[field] === undefined) {
        return
    }
    for (const api of SETTINGS_SYNC_APIS) {
        if (field.startsWith(api)) {
            return
        }
    }
    conditionalDispatch({ type: 'SAVE_USER_FIELD', field, value: state.user.updated[field] }, state, dispatch)
}

export const specialSyncActions = (
    appState: AppContextState,
    appDispatch: Dispatch<PrimaryAction>): void => {
    for (const api of ['fitbit', 'rescuetime'] as const) {
        for (const fieldBase of ['start_date', 'user_id', 'api_key']) {
            const field = `${api}_${fieldBase}`
            const baseValue = appState.user?.row?.[field]
            const modifiedValue = appState.user?.updated?.[field] as string
            if (modifiedValue !== undefined && baseValue !== modifiedValue) {
                const saveAction: PrimaryAction = { type: 'SAVE_USER_FIELD', field, value: modifiedValue }
                conditionalDelayedDispatch(saveAction, appState, appDispatch, 1)
            }
        }
    }
}
